import { IconCheck } from "@tabler/icons-react"
import { Column } from "@tanstack/react-table"

import { cn } from "@/lib/utils"

import { Command, CommandInput, CommandItem, CommandList } from "../../cn/command"
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../DropdownMenu"
import { LE_TableInstance } from "../types"

export function MultiEnumFilter<T extends Record<string, any> = object>({
  col,
  filterValue = [],
  table,
}: {
  col: Column<T>
  filterValue?: string[]
  table: LE_TableInstance<T>
}) {
  const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
  if (typeof label !== "string") {
    throw new Error(
      "column `header` must be a string, or `meta.textLabel` must be set.",
    )
  }

  let filterOptions: [string, string][] = []
  if (!col.columnDef.meta?.filterOptions) {
    if (table.options.manualFiltering) {
      throw new Error(`meta.filterOptions of column "${label}" must be defined`)
    } else {
      const keys = col.getFacetedUniqueValues().keys()
      filterOptions = Array.from(keys).map((v) => [v, v])
    }
  } else {
    filterOptions = Object.entries(col.columnDef.meta.filterOptions)
  }

  const checkedOptions: Record<string, boolean> = {}
  filterValue?.forEach((v) => {
    checkedOptions[v] = true
  })

  return (
    <>
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <div className="-mx-1">
        {filterOptions.length > 0 ? (
          <>
            <DropdownMenuItem
              onSelect={() => {
                col.setFilterValue(filterOptions.map(([v]) => v))
              }}
            >
              Select all
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <Command>
              <CommandInput
                rootClassName="pb-1 px-2"
                className="h-8 py-0"
                placeholder="Search..."
                autoFocus
              />
              <CommandList className="mt-1">
                {filterOptions.map(([value, label]) => (
                  <CommandItem
                    key={label}
                    className="mx-1 flex gap-2"
                    onSelect={() => {
                      col.setFilterValue((old?: string[]) => {
                        const newChecked: Record<string, boolean> = {}
                        if (!checkedOptions[value]) newChecked[value] = true

                        old?.forEach((v) => {
                          if (v !== value) newChecked[v] = true
                        })

                        // Create a new array in the canonical order, without duplicates.
                        const new_: string[] = filterOptions
                          .map(([v]) => v)
                          .filter((v) => newChecked[v])

                        console.log("New filter: %o", new_)
                        if (new_.length) return new_
                        return undefined
                      })
                    }}
                  >
                    <IconCheck
                      size={14}
                      className={cn({ ["invisible"]: !checkedOptions[value] })}
                    />{" "}
                    {label}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </>
        ) : null}
      </div>
    </>
  )
}
